import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { passwordProblem } from '../utils/passwordRules';
import { useAuth, restoreUser } from '../context/AuthContext';
import api from '../utils/api';
import LogoMark from '../components/LogoMark';

function PasswordField({ id, label, value, onChange, autoComplete, tip }) {
  const { t } = useTranslation('login');
  const [show, setShow] = useState(false);
  return (
    <>
      <label htmlFor={id} style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--c-text-body-fg)', marginBottom: 4 }}>
        {label}
      </label>
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          required
          autoComplete={autoComplete}
          placeholder="••••••••"
          title={tip}
          style={{
            display: 'block',
            width: '100%',
            padding: '10px 12px',
            // Room for the show/hide button, which is 44 px wide on a touch screen.
            paddingInlineEnd: 50,
            border: '1px solid var(--c-border-strong)',
            borderRadius: 8,
            fontSize: 14,
            outline: 'none',
            boxSizing: 'border-box',
            transition: 'border-color 0.15s',
          }}
          onFocus={e => e.target.style.borderColor = 'var(--c-primary)'}
          onBlur={e => e.target.style.borderColor = 'var(--c-border-strong)'}
        />
        <button
          type="button"
          onClick={() => setShow(s => !s)}
          title={show ? t('tip.hidePassword') : t('tip.showPassword')}
          aria-label={show ? t('hidePassword') : t('showPassword')}
          style={{
            position: 'absolute', insetInlineEnd: 4, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', fontSize: 16,
            color: 'var(--c-text-muted-fg)', padding: 4, lineHeight: 1,
          }}
        >
          {show ? '🙈' : '👁'}
        </button>
      </div>
    </>
  );
}

/*
 * Two ways in. A FORCED change: the account carries must_change_password (a
 * new account, or after an admin reset), PrivateRoute sends every page here,
 * and the only way out is to finish or to log out. A VOLUNTARY change: the
 * person chose "Change password" in account settings, so the page says so,
 * offers the way back, and returns there when done.
 */
export default function ChangePassword() {
  const { t } = useTranslation(['login', 'account', 'register']);
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const forced = Boolean(user?.must_change_password);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // The server applies the registration rules to a new password; checking
    // them here saves the round trip. Its address is checked there only.
    const problem = passwordProblem(newPassword, user?.username);
    if (problem === 'password_too_short') {
      setError(t('passwordTooShortError'));
      return;
    }
    if (problem) {
      setError(t(`register:errors.${problem}`));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('passwordMismatchError'));
      return;
    }
    setLoading(true);
    try {
      const res = await api.put('/auth/change-password', { current_password: currentPassword, new_password: newPassword });
      login(res.data.token);
      if (forced) navigate('/');
      else navigate('/account', { state: { passwordChanged: true } });
    } catch (err) {
      if (err.response?.status === 401) {
        // The server answers 401 both for a wrong current password and for a
        // session that has lapsed; the token's own expiry tells them apart.
        if (!restoreUser()) { logout(); return; }
        setError(t('currentPasswordWrong'));
      } else if (!err.response) {
        setError(t('serverUnreachable'));
      } else if (err.response.status === 429) {
        // Five wrong current passwords in a quarter hour.
        const minutes = Math.max(1, Math.ceil((err.response.data?.retry_after || 60) / 60));
        setError(t('register:errors.rate_limited', { minutes }));
      } else if (err.response.status === 400 && /^password_/.test(err.response.data?.code || '')) {
        // A registration rule this page could not check (the account's own
        // address), or one it checks differently.
        setError(t(`register:errors.${err.response.data.code}`));
      } else {
        setError(t('changePasswordFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(160deg, var(--c-primary-soft-3) 0%, var(--c-primary-soft-4) 50%, var(--c-primary-soft-5) 100%)',
      padding: 16,
    }}>
      <div style={{
        width: '100%',
        maxWidth: 420,
        background: 'var(--c-surface)',
        borderRadius: 16,
        boxShadow: '0 8px 40px rgba(44, 110, 73, 0.12), 0 2px 8px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '36px 40px 28px',
          borderBottom: '1px solid var(--c-bg-muted-2)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
          <LogoMark alt={t('logoAlt')} />
        </div>

        <div style={{ padding: '28px 40px 36px' }}>
          <p style={{ margin: '0 0 24px', fontSize: 14, color: 'var(--c-text-muted-fg)', textAlign: 'center' }}>
            {forced ? t('changePasswordSubtitle') : t('changePassword.voluntarySubtitle', { ns: 'account' })}
          </p>

          <form onSubmit={handleSubmit}>
            <PasswordField id="currentPassword" label={t('currentPassword')} value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)} autoComplete="current-password" tip={t('tip.currentPassword')} />
            <PasswordField id="newPassword" label={t('newPassword')} value={newPassword}
              onChange={e => setNewPassword(e.target.value)} autoComplete="new-password" tip={t('tip.newPassword')} />
            <PasswordField id="confirmPassword" label={t('confirmPassword')} value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)} autoComplete="new-password" tip={t('tip.confirmPassword')} />

            {error && (
              <div style={{
                background: 'var(--c-danger-soft)',
                color: 'var(--c-danger-2-fg)',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 13,
                marginBottom: 16,
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              title={t('tip.changePassword')}
              style={{
                display: 'block',
                width: '100%',
                padding: '11px',
                background: loading ? 'var(--c-primary-3)' : 'var(--c-primary)',
                color: 'var(--c-on-accent)',
                border: 'none',
                borderRadius: 8,
                fontSize: 15,
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background 0.15s',
                letterSpacing: 0.3,
              }}
            >
              {loading ? t('changingPassword') : t('changePasswordBtn')}
            </button>
          </form>

          {forced ? (
            <button
              type="button"
              onClick={logout}
              title={t('tip.logout')}
              style={{
                display: 'block', width: '100%', textAlign: 'center', marginTop: 16,
                background: 'none', border: 'none', color: 'var(--c-text-muted-fg)', fontSize: 13, cursor: 'pointer',
              }}
            >
              {t('logoutLink')}
            </button>
          ) : (
            <Link
              to="/account"
              title={t('tip.changePasswordBack', { ns: 'account' })}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 44, marginTop: 8,
                color: 'var(--c-primary-fg)', fontSize: 14, fontWeight: 500, textDecoration: 'none',
              }}
            >
              {t('changePassword.back', { ns: 'account' })}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
